import { useEffect, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { knowledgeApi } from '@/lib/api'
import { loadPdfJs } from '@/lib/utils/extract-pdf-text'
import {
  PassageRectPct,
  PdfTextItemLike,
  passageRectsFromTextItems,
} from './pdfPassageRects'

/**
 * Render the cited page from the original PDF and box the passage using the
 * page's own text layer.
 *
 * This is the accurate half of the citation viewer: the raster preview can only
 * approximate where a passage sits, while the PDF carries a transform per text
 * run. Everything here is best-effort — the file may not be a PDF, the blob
 * fetch may fail, and a scanned page has no text layer at all. Any of those
 * calls `onUnavailable`, and the caller falls back to the raster preview with
 * its estimated band.
 */

interface PdfPassagePageProps {
  documentToken: string
  pageNumber: number
  snippet: string
  onUnavailable: () => void
  /** Reports whether the passage could be boxed, so the caller knows whether
   * the reader has actually been shown the quoted words. */
  onPassageLocated?: (located: boolean) => void
}

/** Holds the page's footprint while it loads, so the panel does not collapse to
 * a sliver and then jump when the canvas is sized. */
const LOADING_MIN_HEIGHT_PX = 420

type LoadState = 'loading' | 'ready'

export function PdfPassagePage({
  documentToken,
  pageNumber,
  snippet,
  onUnavailable,
  onPassageLocated,
}: PdfPassagePageProps) {
  const [state, setState] = useState<LoadState>('loading')
  const [rects, setRects] = useState<Array<PassageRectPct>>([])
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const highlightRef = useRef<HTMLDivElement | null>(null)
  const unavailableRef = useRef(onUnavailable)
  unavailableRef.current = onUnavailable
  const passageLocatedRef = useRef(onPassageLocated)
  passageLocatedRef.current = onPassageLocated

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    let pdf: { destroy: () => Promise<void>; numPages: number } | null = null
    let renderTask: { cancel: () => void; promise: Promise<void> } | null = null

    setState('loading')
    setRects([])

    void (async () => {
      try {
        const [{ downloadUrl }, pdfjsLib] = await Promise.all([
          knowledgeApi.downloadDocument(documentToken),
          loadPdfJs(),
        ])
        if (cancelled) {
          if (downloadUrl.startsWith('blob:')) URL.revokeObjectURL(downloadUrl)
          return
        }
        if (!downloadUrl) throw new Error('No download URL for document')
        objectUrl = downloadUrl

        const loaded = await pdfjsLib.getDocument({ url: downloadUrl }).promise
        if (cancelled) {
          void loaded.destroy()
          return
        }
        pdf = loaded

        if (pageNumber < 1 || pageNumber > loaded.numPages) {
          throw new Error('Cited page is outside the document')
        }

        const page = await loaded.getPage(pageNumber)
        const canvas = canvasRef.current
        const container = containerRef.current
        if (cancelled || !canvas || !container) return

        const base = page.getViewport({ scale: 1 })
        if (!base.width) throw new Error('PDF page has no width')

        // Render at the container's width times the device ratio so the page is
        // crisp on retina; the highlight boxes are percentages, so they follow
        // the same viewport regardless of the ratio chosen here.
        const cssWidth = container.clientWidth || 640
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const viewport = page.getViewport({
          scale: (cssWidth / base.width) * ratio,
        })

        canvas.width = Math.ceil(viewport.width)
        canvas.height = Math.ceil(viewport.height)

        renderTask = page.render({ canvas, viewport })
        await renderTask.promise
        if (cancelled) return

        const textContent = await page.getTextContent()
        if (cancelled) return

        const items: Array<PdfTextItemLike> = []
        for (const item of textContent.items) {
          if ('str' in item && 'transform' in item) {
            items.push({
              str: item.str,
              width: item.width,
              transform: Array.from(item.transform),
            })
          }
        }
        const located = passageRectsFromTextItems(items, snippet, {
          transform: Array.from(viewport.transform),
          width: viewport.width,
          height: viewport.height,
          scale: viewport.scale,
        })
        setRects(located)
        setState('ready')
        passageLocatedRef.current?.(located.length > 0)
      } catch {
        if (!cancelled) unavailableRef.current()
      }
    })()

    return () => {
      cancelled = true
      try {
        renderTask?.cancel()
      } catch {
        // Cancelling an already-settled render task is not an error worth surfacing.
      }
      void pdf?.destroy().catch(() => undefined)
      if (objectUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [documentToken, pageNumber, snippet])

  useEffect(() => {
    if (state !== 'ready') return
    highlightRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    })
  }, [state, rects])

  return (
    <div className="space-y-2" data-testid="thread-source-pdf-page">
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-xl border border-border bg-muted/20"
        style={
          state === 'loading' ? { minHeight: LOADING_MIN_HEIGHT_PX } : undefined
        }
      >
        {/* The canvas stays mounted (the render target needs its ref) but is
            kept invisible until the page is painted, so the reader sees the
            spinner rather than a blank sheet. */}
        <canvas
          ref={canvasRef}
          className={cn('block w-full', state === 'loading' && 'invisible')}
        />
        {rects.map((rect, index) => (
          <div
            key={`${rect.topPct}-${rect.leftPct}-${index}`}
            ref={index === 0 ? highlightRef : null}
            className="pointer-events-none absolute rounded-sm bg-primary/35 mix-blend-multiply ring-1 ring-primary/60 dark:mix-blend-screen"
            style={{
              left: `${rect.leftPct}%`,
              top: `${rect.topPct}%`,
              width: `${rect.widthPct}%`,
              height: `${rect.heightPct}%`,
            }}
            data-testid="thread-source-pdf-highlight"
            aria-hidden
          />
        ))}
        {state === 'loading' ? (
          <div
            className="absolute inset-0 flex items-center justify-center gap-2"
            role="status"
            aria-live="polite"
            data-testid="thread-source-pdf-loading"
          >
            <Loader2
              className="h-4 w-4 animate-spin text-muted-foreground"
              aria-hidden
            />
            <Text size="sm" color="muted">
              Loading the original page…
            </Text>
          </div>
        ) : null}
      </div>
      {state === 'ready' && rects.length === 0 ? (
        <Text size="xs" color="muted" data-testid="thread-source-pdf-no-match">
          Showing the cited page from the original file. The quoted words could
          not be found in this page&apos;s text layer, so nothing is boxed.
        </Text>
      ) : null}
    </div>
  )
}
